<script setup>
// [TODO] Use Teleport to move the modal to the end of the body
import { ref, computed, defineEmits, onMounted } from 'vue';
import { useEventListener } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';

const { modalType, closeOnBackdropClick, onClose } = defineProps({
  closeOnBackdropClick: { type: Boolean, default: true },
  showCloseButton: { type: Boolean, default: true },
  onClose: { type: Function, required: true },
  fullWidth: { type: Boolean, default: false },
  modalType: { type: String, default: 'centered' },
  size: { 
    type: String, 
    default: 'default',
    validator: (value) => [
      'xs', 'sm', 'default', 'medium', 'large', 'xl', 'xxl', 'xxxl', 'full'
    ].includes(value)
  },
});

const emit = defineEmits(['close']);
const show = defineModel('show', { type: Boolean, default: false });

const modalClassName = computed(() => {
  const modalClassNameMap = {
    centered: '',
    'right-aligned': 'right-aligned',
  };

  return `modal-mask skip-context-menu ${modalClassNameMap[modalType] || ''}`;
});

// [TODO] Revisit this logic to use outside click directive
const mousedDownOnBackdrop = ref(false);

const handleMouseDown = () => {
  mousedDownOnBackdrop.value = true;
};

const close = () => {
  show.value = false;
  emit('close');
  onClose();
};

const onMouseUp = () => {
  if (mousedDownOnBackdrop.value) {
    mousedDownOnBackdrop.value = false;
    if (closeOnBackdropClick) {
      close();
    }
  }
};

const onKeydown = e => {
  if (show.value && e.code === 'Escape') {
    close();
    e.stopPropagation();
  }
};

useEventListener(document.body, 'mouseup', onMouseUp);
useEventListener(document, 'keydown', onKeydown);

onMounted(() => {
  if (import.meta.env.DEV && onClose && typeof onClose === 'function') {
    // eslint-disable-next-line no-console
    console.warn(
      "[DEPRECATED] The 'onClose' prop is deprecated. Please use the 'close' event instead."
    );
  }
});
</script>

<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      :class="modalClassName"
      transition="modal"
      @mousedown="handleMouseDown"
    >
      <div
        class="relative max-h-full overflow-auto bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu"
        :class="{
          'rounded-xl': !fullWidth && size !== 'full',
          'items-center rounded-none flex h-full justify-center w-full':
            fullWidth || size === 'full',
          [size]: true,
        }"
        @mouse.stop
        @mousedown="event => event.stopPropagation()"
      >
        <Button
          v-if="showCloseButton"
          ghost
          slate
          icon="i-lucide-x"
          class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
          @click="close"
        />
        <slot />
      </div>
    </div>
  </transition>
</template>

<style lang="scss">
.modal-mask {
  @apply flex items-center justify-center bg-n-alpha-black2 backdrop-blur-[4px] z-[9990] h-full left-0 fixed top-0 w-full;

  .modal-container {
    // Tamanho padrão (quando size não é especificado ou size="default")
    @apply w-[37.5rem]; // 600px

    // Extra Small - Para confirmações simples, alertas
    &.xs {
      @apply max-w-[95%] w-[20rem]; // 320px
    }

    // Small - Para formulários pequenos, confirmações com mais conteúdo
    &.sm {
      @apply max-w-[95%] w-[28rem]; // 448px
    }

    // Default já está aplicado na classe base
    &.default {
      @apply max-w-[90%] w-[37.5rem]; // 600px
    }

    // Medium - Para formulários médios, conteúdo moderado
    &.medium {
      @apply max-w-[85%] w-[56.25rem]; // 900px
    }

    // Large - Para formulários complexos, tabelas pequenas
    &.large {
      @apply max-w-[90%] w-[75rem]; // 1200px
    }

    // Extra Large - Para dashboards, tabelas grandes
    &.xl {
      @apply max-w-[95%] w-[87.5rem]; // 1400px
    }

    // Extra Extra Large - Para conteúdo muito extenso
    &.xxl {
      @apply max-w-[98%] w-[100rem]; // 1600px
    }

    // Triple Extra Large - Para aplicações complexas
    &.xxxl {
      @apply max-w-[99%] w-[112.5rem]; // 1800px
    }

    // Full - Ocupa toda a tela (como fullWidth mas com controle via size)
    &.full {
      @apply w-full h-full max-w-none rounded-none;
    }

    .content {
      @apply p-8;
    }

    form,
    .modal-content {
      @apply pt-4 pb-8 px-8 self-center;

      a {
        @apply p-4;
      }
    }
  }
}

.modal-big {
  @apply w-full;
}

.modal-mask.right-aligned {
  @apply justify-end;

  .modal-container {
    @apply rounded-none h-full w-[30rem];

    // Tamanhos específicos para modal right-aligned
    &.xs { @apply w-[20rem]; }
    &.sm { @apply w-[25rem]; }
    &.default { @apply w-[30rem]; }
    &.medium { @apply w-[35rem]; }
    &.large { @apply w-[40rem]; }
    &.xl { @apply w-[45rem]; }
    &.xxl { @apply w-[50rem]; }
    &.xxxl { @apply w-[55rem]; }
    &.full { @apply w-full; }
  }
}

.modal-enter,
.modal-leave {
  @apply opacity-0;
}

.modal-enter .modal-container,
.modal-leave .modal-container {
  transform: scale(1.1);
}

/* Responsividade para telas menores */
@media (max-width: 768px) {
  .modal-container {
    &.xs, &.sm, &.default { @apply w-[95%]; }
    &.medium { @apply w-[98%]; }
    &.large, &.xl, &.xxl, &.xxxl { @apply w-[99%]; }
  }
}

@media (max-width: 480px) {
  .modal-container {
    &.xs, &.sm, &.default, &.medium, &.large, &.xl, &.xxl, &.xxxl { 
      @apply w-[100%] h-full rounded-none; 
    }
  }
}
</style>